import React from "react"
import { bindActionCreators } from "redux"
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import colors from "../../Constants/colors"

import PYPLayoutContainer from "../PYPLayoutContainer/PYPLayoutContainer.js"
import PYPPartTitleContent from "../PYPPartTitleContent/PYPPartTitleContent.js"
import PYPInputBox from "../PYPInputBox/PYPInputBox.js"
import SkeletonCard from "../SkeletonCard/SkeletonCard.js"

import Actions from "../../Redux/Actions"
import { ADDITIONAL_COURSE_ID } from "../../Constants/courses"
import { isCourseBuilderEndOfCourse } from "../../Utils/workshopNavigation"

function getInputTitleId(box) {
  return Number(box?.input_title_id ?? box?.inputTitleId)
}

function resolveBoxInput(inputBoxes, inputTitle, index, claimedBoxIds, validTitleIds) {
  const titleId = Number(inputTitle.id)
  const boxes = (inputBoxes || []).filter(
    (row) => row.id && !String(row.id).startsWith("temp_")
  )

  const byId = boxes.find((box) => {
    const boxTitleId = getInputTitleId(box)
    return !Number.isNaN(boxTitleId) && boxTitleId === titleId
  })
  if (byId) return byId

  if (index < boxes.length) {
    const candidate = boxes[index]
    if (candidate && candidate.id && !claimedBoxIds.has(candidate.id)) {
      const boxTitleId = getInputTitleId(candidate)
      const isOrphan =
        Number.isNaN(boxTitleId) || !validTitleIds.has(boxTitleId)
      if (isOrphan) return candidate
    }
  }

  return (
    boxes.find((box) => {
      if (!box.id || claimedBoxIds.has(box.id)) return false
      const boxTitleId = getInputTitleId(box)
      return Number.isNaN(boxTitleId) || !validTitleIds.has(boxTitleId)
    }) || null
  )
}

class LayoutMetaDataUserInput extends React.Component {
  constructor(props) {
    super(props)
    this.pendingValues = {} // Store values typed before inputs are initialized
  }

  componentDidMount() {
    if (!this.props.pyp.savingPYP) {
      const currentPartId = this.props.pyp?.navigation?.currentPartId
      const isCourseBuilder =
        Number(this.props.pyp?.navigation?.currentCourseId) === ADDITIONAL_COURSE_ID
      const currentPart = this.props.pyp?.parts?.byId?.[currentPartId]
      const loadedPartDataId = currentPart?.partData
      const partDataAlreadyLoaded =
        loadedPartDataId &&
        this.props.pyp?.partData?.byId?.[loadedPartDataId]?.data != null

      console.log('[LayoutMetaDataUserInput] componentDidMount - fetching part data', {
        currentPartId,
        partDataId: this.props.partDataId,
        hasMetadata: !!this.props.metadata,
        initializedPart: this.props.pyp?.initializedPart
      })

      if (isCourseBuilder && partDataAlreadyLoaded) {
        this.checkForUninitializedInputs()
        return
      }

      // Only fetch if we have a valid currentPartId (not -1 or undefined)
      if (currentPartId && currentPartId !== -1) {
      this.props.fetchPartData()
        .then(() => {
          console.log('[LayoutMetaDataUserInput] fetchPartData completed', {
            partDataId: this.props.partDataId,
            inputTitles: this.props.inputTitles?.length || 0,
            inputBoxes: this.props.inputBoxes?.length || 0,
              fetchingPartData: this.props.fetchingPartData,
              metadata: this.props.metadata
          })
          this.checkForUninitializedInputs()
        })
        .catch((error) => {
          console.error('[LayoutMetaDataUserInput] fetchPartData failed:', error)
            // Even on error, try to check for uninitialized inputs in case some data loaded
            this.checkForUninitializedInputs()
        })
      } else {
        console.warn('[LayoutMetaDataUserInput] Skipping fetchPartData - currentPartId is not valid:', currentPartId)
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Do not refetch when save completes: savePYP already persisted to the server and
    // Redux holds the latest text. Refetching here dispatches FETCHING_PART_DATA synchronously
    // during the SAVED_PYP render, which runs *before* incrementPartId/decrementPartId in the
    // Next/Previous handler — incrementPartId then no-ops because fetchingPartData is true.
    // After part change, remount + componentDidMount fetch loads the new part.
    // If inputs were just initialized, apply any pending values
    if (
      prevProps.inputBoxes.length !== this.props.inputBoxes.length ||
      prevProps.inputTitles !== this.props.inputTitles
    ) {
      this.applyPendingValues()
    }

    const isCourseBuilder =
      Number(this.props.pyp?.navigation?.currentCourseId) === ADDITIONAL_COURSE_ID

    if (isCourseBuilder) {
      const inputTitlesArray = Array.isArray(this.props.inputTitles)
        ? this.props.inputTitles
        : (this.props.inputTitles ? Object.values(this.props.inputTitles) : [])
      const hadNoInputs =
        (!inputTitlesArray || inputTitlesArray.length === 0) &&
        (!this.props.inputBoxes || this.props.inputBoxes.length === 0)
      const savingFinished =
        prevProps.pyp.savingPYP && !this.props.pyp.savingPYP

      // About to Finish → /home; do not refetch or Next/Finish can no-op on fetchingPartData.
      if (isCourseBuilderEndOfCourse(this.props.pyp)) {
        return
      }

      if (
        savingFinished &&
        hadNoInputs &&
        !this.props.fetchingPartData &&
        this.props.pyp?.navigation?.currentPartId > 0
      ) {
        this.props
          .fetchPartData()
          .then(() => this.checkForUninitializedInputs())
          .catch(() => this.checkForUninitializedInputs())
      }
    }
  }

  applyPendingValues() {
    const inputTitlesArray = Array.isArray(this.props.inputTitles)
      ? this.props.inputTitles
      : (this.props.inputTitles ? Object.values(this.props.inputTitles) : [])
    const validTitleIds = new Set(inputTitlesArray.map((t) => Number(t.id)))
    const claimedBoxIds = new Set()

    inputTitlesArray.forEach((inputTitle, index) => {
      const boxInput = resolveBoxInput(
        this.props.inputBoxes,
        inputTitle,
        index,
        claimedBoxIds,
        validTitleIds
      )
      if (boxInput?.id) claimedBoxIds.add(boxInput.id)

      const pendingValue = this.pendingValues[inputTitle.id]
      if (boxInput && boxInput.id && pendingValue !== undefined) {
        this.props.setPYPInputText(
          this.props.partDataId,
          boxInput.id,
          pendingValue
        )
        this.props.setPYPInputText(
          this.props.partDataId,
          `temp_${inputTitle.id}`,
          ""
        )
        delete this.pendingValues[inputTitle.id]
      }
    })
  }

  checkForUninitializedInputs() {
    const inputTitles = this.props.inputTitles
    if (!inputTitles || inputTitles.length === 0) {
      if (this.props.inputBoxes.length <= 0) {
        console.warn('[LayoutMetaDataUserInput] Cannot initialize inputs - no inputTitles available', {
          partDataId: this.props.partDataId,
          hasPartData: !!this.props.partDataId,
          fetchingPartData: this.props.fetchingPartData
        })
      }
      return
    }

    const missingTitles = inputTitles.filter((title) => {
      return !this.props.inputBoxes.some((box) => {
        return getInputTitleId(box) === Number(title.id)
      })
    })

    if (missingTitles.length > 0) {
      console.log('[LayoutMetaDataUserInput] Initializing missing inputs', {
        partDataId: this.props.partDataId,
        missingCount: missingTitles.length,
        inputTitlesCount: inputTitles.length
      })
      this.props.initializePYPInputs(
        this.props.partDataId,
        missingTitles
      )
    }
  }

  renderInputBoxes() {
    // If still fetching part data, show skeleton
    if (this.props.fetchingPartData) {
      const skeleton = (i) => (
        <SkeletonCard
          key={i}
          size={this.props.inputSize}
          style={{ margin: "30px 30px 0px 30px" }}
        />
      )
      if (this.props.numInputs) {
        return Array(this.props.numInputs)
          .fill(1)
          .map((e, i) => skeleton(i))
      }
      return skeleton(0)
    }
    
    // If inputTitles exist, render input boxes (even if inputBoxes is empty)
    // Only show skeleton if inputTitles are not yet loaded
    const inputTitlesArray = Array.isArray(this.props.inputTitles) 
      ? this.props.inputTitles 
      : (this.props.inputTitles ? Object.values(this.props.inputTitles) : [])
    
    // If we have inputBoxes but no inputTitles, still render them (they might have been created without titles)
    // This is especially important when showing inputs from multiple parts (last part of module)
    if ((!inputTitlesArray || inputTitlesArray.length === 0) && this.props.inputBoxes && this.props.inputBoxes.length > 0) {
      console.log('[LayoutMetaDataUserInput] Rendering inputBoxes without inputTitles:', {
        inputBoxesCount: this.props.inputBoxes.length,
        inputBoxes: this.props.inputBoxes.map(box => ({
          id: box.id,
          part_id: box.part_id,
          title: box.title?.title,
          hasTitle: !!box.title,
          text: box.text?.substring(0, 30)
        }))
      })
      
      // Render based on existing inputBoxes
      return this.props.inputBoxes.map((boxInput, key) => {
        // Ensure text is always a string (handle null/undefined)
        const displayText = boxInput.text !== null && boxInput.text !== undefined 
          ? String(boxInput.text) 
          : (this.pendingValues[boxInput.input_title_id] || "")
        
        // Use title from the input itself if available (for inputs from other parts)
        const inputTitle = boxInput.title?.title || ""
        
        return (
          <PYPInputBox
            key={key}
            text={displayText}
            placeholder=""
            style={{ margin: "30px 30px 0px 30px" }}
            title={inputTitle}
            size={boxInput.size || "small"}
            valueDidChange={(value) => {
              if (boxInput && boxInput.id) {
                this.props.setPYPInputText(
                  this.props.partDataId,
                  boxInput.id,
                  value
                )
              }
            }}
            showIncomplete={this.props.pyp.navigation.showIncompletionError}
          />
        )
      })
    }
    
    // If we have neither inputTitles nor inputBoxes, show skeleton only if still fetching
    // If fetching is done but no data, this part might not have input fields
    if ((!inputTitlesArray || inputTitlesArray.length === 0) && (!this.props.inputBoxes || this.props.inputBoxes.length === 0)) {
      // If partData exists (metadata loaded) but no inputs, this part doesn't have input fields
      if (this.props.partDataId && this.props.metadata && !this.props.fetchingPartData) {
        // Part data is loaded but no inputs - return null (no input fields for this part)
        return null
      }
      
      // Still loading or no partData yet, show skeleton
      const skeleton = (i) => (
        <SkeletonCard
          key={i}
          size={this.props.inputSize}
          style={{ margin: "30px 30px 0px 30px" }}
        />
      )
      if (this.props.numInputs) {
        return Array(this.props.numInputs)
          .fill(1)
          .map((e, i) => skeleton(i))
      }
      return skeleton(0)
    }

    // inputTitles is already converted to array above, use it directly
    const validTitleIds = new Set(inputTitlesArray.map((t) => Number(t.id)))
    const claimedBoxIds = new Set()

    return inputTitlesArray.map((inputTitle, key) => {
      const boxInput = resolveBoxInput(
        this.props.inputBoxes,
        inputTitle,
        key,
        claimedBoxIds,
        validTitleIds
      )
      if (boxInput?.id) claimedBoxIds.add(boxInput.id)

      const displayText = boxInput
        ? (boxInput.text !== null && boxInput.text !== undefined ? String(boxInput.text) : "")
        : (this.pendingValues[inputTitle.id] || "")

      return (
        <PYPInputBox
          key={key}
          text={displayText}
          placeholder=""
          style={{ margin: "30px 30px 0px 30px" }}
          title={inputTitle.title}
          size={boxInput ? boxInput.size : "small"}
          valueDidChange={(value) => {
            if (boxInput && boxInput.id) {
              this.props.setPYPInputText(
                this.props.partDataId,
                boxInput.id,
                value
              )
            } else {
              this.pendingValues[inputTitle.id] = value

              this.props.setPYPInputText(
                this.props.partDataId,
                `temp_${inputTitle.id}`,
                value
              )

              this.forceUpdate()

              const missingTitles = inputTitlesArray.filter((title) => {
                return !this.props.inputBoxes.some((box) => {
                  return getInputTitleId(box) === Number(title.id)
                })
              })
              if (missingTitles.length > 0) {
                console.log('[LayoutMetaDataUserInput] Initializing inputs after user typed in uninitialized field')
                this.props.initializePYPInputs(
                  this.props.partDataId,
                  missingTitles
                )
              }
            }
          }}
          showIncomplete={this.props.pyp.navigation.showIncompletionError}
        />
      )
    })
  }

  render() {
    const inputBoxes = this.renderInputBoxes()
    return (
      <PYPLayoutContainer
        backgroundColor={colors.background}
        style={{ ...this.props.style }}
      >
        <PYPPartTitleContent
          title={this.props.metadata && this.props.metadata.title}
          description={this.props.metadata && this.props.metadata.description}
        />
        <div style={styles.inputBoxContainer}>{inputBoxes}</div>
      </PYPLayoutContainer>
    )
  }
}

const styles = {
  inputBoxContainer: {
    width: "100%",
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: "30px"
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch)
}

  const mapStateToProps = (state, props) => {
  const currentPart = state.pyp.parts.byId[state.pyp.navigation.currentPartId]
  const partDataId = currentPart?.partData

  let metadata = { title: null, description: null }
  let inputBoxes = []
  let inputTitles = []

  // The partData will get fetched separately
  const partData =
    partDataId &&
    state.pyp.partData.byId[partDataId] &&
    state.pyp.partData.byId[partDataId].data
  if (partData) {
    metadata = partData.title?.[0] || { title: null, description: null }
    // Filter out temporary inputs when displaying (they're only for completion checking)
    inputBoxes = (partData.inputs || []).filter((input) => {
      return input.id && (typeof input.id !== "string" || !input.id.toString().startsWith("temp_"))
    })
    inputTitles = partData.inputTitle || []
    
    // Debug logging to help diagnose loading issues
    if (inputBoxes.length > 0) {
      console.log('[LayoutMetaDataUserInput] mapStateToProps - inputBoxes found:', {
        inputBoxesCount: inputBoxes.length,
        inputTitlesCount: inputTitles.length,
        firstInput: {
          id: inputBoxes[0]?.id,
          part_id: inputBoxes[0]?.part_id,
          hasTitle: !!inputBoxes[0]?.title,
          titleText: inputBoxes[0]?.title?.title,
          hasText: !!inputBoxes[0]?.text,
          textPreview: inputBoxes[0]?.text?.substring(0, 30)
        }
      })
    }
    
    // Debug logging for various input types (Cost, Action, Result, Morning, Afternoon, Evening, Constraint, Question, Accomplished, etc.)
    if (metadata.title && (
      metadata.title.toLowerCase().includes('cost') || 
      metadata.title.toLowerCase().includes('action') ||
      metadata.title.toLowerCase().includes('reward') ||
      metadata.title.toLowerCase().includes('result') ||
      metadata.title.toLowerCase().includes('morning') ||
      metadata.title.toLowerCase().includes('afternoon') ||
      metadata.title.toLowerCase().includes('evening') ||
      metadata.title.toLowerCase().includes('unfold') ||
      metadata.title.toLowerCase().includes('constraint') ||
      metadata.title.toLowerCase().includes('limiting') ||
      metadata.title.toLowerCase().includes('factor') ||
      metadata.title.toLowerCase().includes('question') ||
      metadata.title.toLowerCase().includes('cultural') ||
      metadata.title.toLowerCase().includes('suitability') ||
      metadata.title.toLowerCase().includes('accomplished') ||
      metadata.title.toLowerCase().includes('military') ||
      metadata.title.toLowerCase().includes('narrative')
    )) {
      console.log('[LayoutMetaDataUserInput] Inputs data:', {
        partId: currentPart?.id,
        metadataTitle: metadata.title,
        inputBoxesCount: inputBoxes.length,
        inputBoxes: inputBoxes.map(box => ({
          id: box.id,
          input_title_id: box.input_title_id,
          title: box.title?.title,
          text: box.text?.substring(0, 30),
          hasText: !!box.text,
          textType: typeof box.text
        }))
      })
    }
  }

  // don't sort if every inputtitle doesn't have a seq
  const dontSort = inputTitles.some((inputTitle) => !inputTitle.sequence)

  return {
    ...props,
    pyp: state.pyp,
    currentPart,
    partDataId,
    metadata,
    inputBoxes,
    inputTitles: dontSort
      ? inputTitles
      : [...inputTitles].sort((a, b) => (a.sequence > b.sequence ? 1 : -1)),
    initializedPart: state.pyp.initializedPart,
    fetchingPartData: state.pyp.fetchingPartData,
    initializingPYPInputs: state.pyp.initializingPYPInputs
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LayoutMetaDataUserInput)
)
